import { queryClient } from '@/libs/react-query';
import { UsercryptCreateConditionRequest } from '@calm/evexia-web-sdk';
import { Check } from '@mui/icons-material';
import useSaveConditions from '@rhg/hooks/use-save-conditions';
import {
    formatCondition,
    FormattedCondition,
    getAddedConditions,
    getRemovedConditions,
    sortConditions,
} from '@rhg/utils/conditions';
import {
    MouseEventHandler,
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useActiveCircle } from '../use-active-circle';
import useAppConfig from '../use-app-config';
import {
    circleConditionsQueryOptions,
    useCircleConditions,
} from '../use-circle-conditions';
import { useConditions } from '../use-conditions';
import useDeleteCondition from '../use-delete-condition';

export type SelectableCondition = FormattedCondition & {
    isSelected: boolean;
    endIcon: ReactElement | null;
    onClick: MouseEventHandler<HTMLButtonElement>;
};

export function useFormattedConditions(circleId: number) {
    const {
        NEXT_PUBLIC_CONDITIONS_HIERARCHY_ENABLED: conditionsHierarchyEnabled,
    } = useAppConfig();
    const { activeCircle } = useActiveCircle();
    const { saveConditions: saveCondtionsViaApi } =
        useSaveConditions(activeCircle);
    const { deleteCondition } = useDeleteCondition(activeCircle);

    const [selectedConditions, setSelectedConditions] = useState<
        SelectableCondition[]
    >([]);
    const [searchTerm, setSearchTerm] = useState<string | undefined>();
    const [hasUpdatedConditions, setHasUpdatedConditions] = useState(false);
    const { conditions } = useConditions();
    const { conditions: circleConditions, ...otherProps } =
        useCircleConditions(circleId);

    const clearConditions = useCallback(() => setSelectedConditions([]), []);
    const onChoose = useCallback((condition: SelectableCondition) => {
        const id = condition.readableId!;
        setSelectedConditions((prevChoices) =>
            prevChoices.some((choice) => choice.readableId === id)
                ? prevChoices.filter((choice) => choice.readableId !== id)
                : [...prevChoices, condition]
        );
    }, []);

    useEffect(() => {
        // Set initially selected choices based on circle conditions
        if (circleConditions?.conditions?.length) {
            setSelectedConditions(
                circleConditions.conditions.map((condition) => {
                    const supportedCondition = conditions?.conditions?.find(
                        (supportedCondition) =>
                            supportedCondition.id === condition.configId
                    );

                    const formattedConditionResponse = {
                        ...formatCondition(supportedCondition!),
                        isSelected: true,
                        endIcon: <Check />,
                        onClick: () => onChoose(formattedConditionResponse),
                    };
                    return formattedConditionResponse;
                })
            );
        }
    }, [circleConditions, conditions, onChoose]);

    const supportedConditions = useMemo(
        () =>
            conditions?.conditions
                ?.map<SelectableCondition>((condition) => {
                    const isSelected = selectedConditions.some(
                        (selected) =>
                            selected.readableId === condition.readableId
                    );

                    const addedConditionMetadata =
                        circleConditions?.conditions?.find(
                            (circleCondition) =>
                                circleCondition.configId === condition.id
                        );
                    const formattedConditionResponse = {
                        ...formatCondition(condition, addedConditionMetadata),
                        isSelected,
                        endIcon: isSelected ? <Check /> : null,
                        onClick: () => onChoose(formattedConditionResponse),
                    };

                    return formattedConditionResponse;
                })
                ?.sort(sortConditions) ?? [],
        [conditions, selectedConditions, circleConditions, onChoose]
    );

    const saveConditions = useCallback(async () => {
        // Find newly added choices
        const newlyAddedChoices = getAddedConditions({
            selectedConditions,
            circleConditions,
        });

        // Find removed choices
        const removedChoices = getRemovedConditions({
            supportedConditions,
            selectedConditions,
            circleConditions,
        });

        const updatePromises = [];
        if (newlyAddedChoices.length) {
            const formattedChoices = newlyAddedChoices.map(
                (choice) =>
                    ({ configId: choice.id } as UsercryptCreateConditionRequest)
            );
            updatePromises.push(saveCondtionsViaApi(formattedChoices));
        }

        if (removedChoices?.length) {
            removedChoices.forEach((choice) => {
                updatePromises.push(
                    deleteCondition(choice.addedConditionMetadata!.id)
                );
            });
        }
        queryClient.setQueryData(
            circleConditionsQueryOptions.queryKey(activeCircle!.recipient.id!),
            {
                conditions: selectedConditions.map((condition) => ({
                    configId: condition.id,
                })),
            }
        );

        await Promise.all(updatePromises);
    }, [
        activeCircle,
        circleConditions,
        deleteCondition,
        saveCondtionsViaApi,
        selectedConditions,
        supportedConditions,
    ]);

    // TODO: Remove this once the conditionsHierarchyEnabled FF is removed
    const addedConditions = useMemo(
        () =>
            supportedConditions.filter(
                (condition) => !!condition.addedConditionMetadata
            ),
        [supportedConditions]
    );

    useEffect(() => {
        // The old conditions component will just manipulate the state directly, since it messes with this when it calls save/delete on each change.
        if (conditionsHierarchyEnabled) {
            const added = getAddedConditions({
                selectedConditions,
                circleConditions,
            });
            const removed = getRemovedConditions({
                supportedConditions,
                selectedConditions,
                circleConditions,
            });
            if (added.length || removed?.length) {
                setHasUpdatedConditions(true);
            } else if (!added.length && !removed?.length) {
                setHasUpdatedConditions(false);
            }
        }
    }, [
        selectedConditions,
        circleConditions,
        supportedConditions,
        conditionsHierarchyEnabled,
    ]);

    const displayedConditions = useMemo(
        () =>
            searchTerm
                ? supportedConditions.filter((condition) =>
                      condition.searchTerms.some((term) =>
                          term.toLowerCase().includes(searchTerm.toLowerCase())
                      )
                  )
                : supportedConditions,
        [searchTerm, supportedConditions]
    );

    return {
        supportedConditions,
        displayedConditions,
        onSearch: setSearchTerm,
        hasUpdatedConditions,
        // TODO: Remove 'setHasUpdatedConditions' from return object once the conditionsHierarchyEnabled FF is removed
        setHasUpdatedConditions,
        addedConditions,
        selectedConditions,
        clearConditions,
        saveConditions,
        ...otherProps,
    };
}
